import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios'; // Install axios or use fetch API
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleDown, faEnvelope, faHourglassHalf, faObjectUngroup, faUser } from '@fortawesome/free-regular-svg-icons';
import { faChartDiagram, faCircleStop, faCode, faDiagramPredecessor, faLandmark, faPaperPlane, faPlayCircle, faPlug, faProjectDiagram, faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { FaCalendar, FaCalendarWeek, FaOptinMonster, FaRegCalendarTimes, FaUserTimes, FaWatchmanMonitoring, FaWizardsOfTheCoast } from 'react-icons/fa';


const PaletteContainer = styled.div`
  width: 250px;
  background: white;
  border-right: 1px solid #e0e0e0;
  padding: 1rem;
  height: calc(100dvh - 160px);
  overflow-y: auto;
`;

const PaletteTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const PaletteSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
`;

const NodeItem = styled.div`
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  background: ${props => props.color || '#f8f9fa'};
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: grab;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
`;

const NodeIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.8rem;
  font-size: 1.2rem;
  color: ${props => props.iconColor || 'var(--text-primary)'};
`;

const NodeLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
`;

// useEffect((e)=>{
//   const = 
// },[])

const NodePalette = () => {
  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodeData', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <PaletteContainer>
      <PaletteTitle>Component Palette</PaletteTitle>

      <PaletteSection>
        <SectionTitle>Flow Control</SectionTitle>
        <NodeItem
          color="#FFF4CC "
          onDragStart={(event) => onDragStart(event, 'triggerNode', {
            name: 'Trigger',
            description: 'Starting point of the pipeline',
            type: 'trigger',
            triggerType: 'manual',
            schedule: '',
            conditions: []
          })}
          draggable
        >
          <NodeIcon iconColor="#E6C200"><FontAwesomeIcon icon={faPlayCircle} /></NodeIcon>
          <NodeLabel>Trigger</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#FFE5CC"
          onDragStart={(event) => onDragStart(event, 'outputNode', {
            name: 'Output',
            description: 'End point of the pipeline',
            type: 'output',
            outputType: 'response',
            format: 'json',
            destination: ''
          })}
          draggable
        >
          <NodeIcon iconColor="#FF9900"><FontAwesomeIcon icon={faCircleStop} /></NodeIcon>
          <NodeLabel>Output</NodeLabel>
        </NodeItem>
      </PaletteSection>

      <PaletteSection>
        <SectionTitle>Process</SectionTitle>
        <NodeItem
          color="#CCF2FF"
          onDragStart={(event) => onDragStart(event, 'agentNode', {
            name: 'Agent',
            description: 'Design, manage, and monitor your AI agents from a single dashboard',
            type: 'decision_agent'
          })}
          draggable
        >
          <NodeIcon iconColor="#0099CC"><FontAwesomeIcon icon={faUser} /></NodeIcon>
          <NodeLabel>Agent</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#CCE5FF"
          onDragStart={(event) => onDragStart(event, 'modelNode', {
            name: 'Model',
            description: 'All AI models used within the application',
            type: 'model',
          })}
          draggable
        >
          <NodeIcon iconColor="#337AB7"><FontAwesomeIcon icon={faProjectDiagram} /></NodeIcon>
          <NodeLabel>Model</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#E0F7FA"
          onDragStart={(event) => onDragStart(event, 'knowledgeNode', {
            name: 'Knowledge Base',
            description: 'Access to knowledge base',
            type: 'knowledgebase',
          })}
          draggable
        >
          <NodeIcon iconColor="#00ACC1"><FontAwesomeIcon icon={faLandmark} /></NodeIcon>
          <NodeLabel>Knowledge Base</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#D9F9F2"
          onDragStart={(event) => onDragStart(event, 'dataSourceNode', {
            name: 'Data Source',
            description: 'Connection to external data source',
            type: 'source',
          })}
          draggable
        >
          <NodeIcon iconColor="#26A69A"><FontAwesomeIcon icon={faDiagramPredecessor} /></NodeIcon>
          <NodeLabel>Data Source</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#EFE9E3"
          onDragStart={(event) => onDragStart(event, 'manualTaskNode', {
            name: 'Manual Task',
            description: 'Configure a manual review/decision task with AI insights.',
            type: 'manual_task',
          })}
          draggable
        >
          <NodeIcon iconColor="#829591"><FontAwesomeIcon icon={faHourglassHalf} /></NodeIcon>
          <NodeLabel>Manual Task</NodeLabel>
        </NodeItem>

      </PaletteSection>

      <PaletteSection>
        <SectionTitle>Tools</SectionTitle>
        <NodeItem
          color="#E0F7E9"
          onDragStart={(event) => onDragStart(event, 'apiNode', {
            name: 'API Tool',
            description: 'External API integration',
            type: 'API',
          })}
          draggable
        >
          <NodeIcon iconColor="#2E7D32"><FontAwesomeIcon icon={faPlug} /></NodeIcon>
          <NodeLabel>API Tool</NodeLabel>
        </NodeItem>


        <NodeItem
          color="#D6F5E3"
          onDragStart={(event) => onDragStart(event, 'classifierNode', {
            name: 'Classifier',
            description: 'Classify Data',
            type: 'classify',
            parameters: {}
          })}
          draggable
        >
          <NodeIcon iconColor="#388E3C"><FontAwesomeIcon icon={faObjectUngroup} /></NodeIcon>
          <NodeLabel>Classifier</NodeLabel>
        </NodeItem>

        
        <NodeItem
          color="#E4FBE9"
          onDragStart={(event) => onDragStart(event, 'codeNode', {
            name: 'Code Execution',
            description: 'Execute code snippets',
            type: 'code',
          })}
          draggable
        >
          <NodeIcon iconColor="#43A047"><FontAwesomeIcon icon={faCode} /></NodeIcon>
          <NodeLabel>Code Execution</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#D2F8D2"
          onDragStart={(event) => onDragStart(event, 'webHookNode', {
            name: 'Webhook',
            description: 'Send data to external systems',
            type: 'webhook',
          })}
          draggable
        >
          <NodeIcon iconColor="#4CAF50"><FontAwesomeIcon icon={faPaperPlane} /></NodeIcon>
          <NodeLabel>Webhook</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#EFE9E3"
          onDragStart={(event) => onDragStart(event, 'emailOutNode', {
            name: 'Send Email',
            description: 'Send Email to users',
            type: 'emailout',
          })}
          draggable
        >
          <NodeIcon iconColor="#829591"><FontAwesomeIcon icon={faEnvelope} /></NodeIcon>
          <NodeLabel>Send Email</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#E0F7FA"
          onDragStart={(event) => onDragStart(event, 'documentNode', {
            name: 'Document conversion',
            description: 'Generate PDF document from HTML template',
            type: 'document'
          })}
          draggable
        >
          <NodeIcon iconColor="#00ACC1"><FontAwesomeIcon icon={faChartDiagram} /></NodeIcon>
          <NodeLabel>Document conversion</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#F0F8FF"
          onDragStart={(event) => onDragStart(event, 'fileNode', {
            name: 'File Operations',
            description: 'File transfer and operations via SFTP, S3, or ADB',
            type: 'file_input',
            protocol: 'SFTP',
            server_host: '',
            user_name: '',
            port: 22,
            password: '',
            source_location: '',
            sftp_operation: 'MOVE',
            destination_location: '',
            sort_order: 'ASCENDING',
            num_of_files: 1
          })}
          draggable
        >
          <NodeIcon iconColor="#4169E1"><FontAwesomeIcon icon={faChartDiagram} /></NodeIcon>
          <NodeLabel>File Operations</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#E6F1FF"
          onDragStart={(event) => onDragStart(event, 'plannerNode', {
            name: 'Planner',
            description: 'Execute a planner against a query',
            type: 'planner',
            planner_cd: '',
            query_code_block: { lang: 'python', code_block: '' },
            output_schema: null,
          })}
          draggable
        >
          <NodeIcon iconColor="#2F80ED"><FontAwesomeIcon icon={faProjectDiagram} /></NodeIcon>
          <NodeLabel>Planner</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#FFF4E6"
          onDragStart={(event) => onDragStart(event, 'entityOperationNode', {
            name: 'Entity Operation',
            description: 'Save, delete, or fetch entities',
            type: 'entity_operation',
            entity_cd: '',
            entity_operation: 'save',
            entity_data_code_block: {
              lang: 'python',
              code_block: "entity = request.get('content')\nentity['id']=0",
            },
            filter_query_code_block: {
              lang: 'python',
              code_block: '',
            },
            entity_output_code_block: {
              lang: 'python',
              code_block: "response = entity_output\nworkflow_vars['policy_rules_and_insight_agent_response'] = entity_output",
            },
            sort_query_code_block: null,
            limit_code_block: null,
            skip_code_block: null,
          })}
          draggable
        >
          <NodeIcon iconColor="#F2994A"><FontAwesomeIcon icon={faProjectDiagram} /></NodeIcon>
          <NodeLabel>Entity Operation</NodeLabel>
        </NodeItem>

        <NodeItem
          color="#ECF4E8"
          onDragStart={(event) => onDragStart(event, 'timerNode', {
            name: 'Timer',
            description: 'Configure, execute, and pause the pipeline.',
            type: 'timer_node'
          })}
          draggable
        >
          <NodeIcon iconColor="#63A361"><FontAwesomeIcon icon={faCalendarTimes} /></NodeIcon>
          <NodeLabel>Timer</NodeLabel>
        </NodeItem>
      </PaletteSection>
    </PaletteContainer>
  );
};

export default NodePalette;
